import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faImageSlash } from '@fortawesome/pro-regular-svg-icons';
import { faFile, faFilePdf, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { SkeletonLoader } from '../ui/Image';
import { Kind } from '../upload2';

type Props = {
  kind: Kind;
  url: string;
  name?: string;
  onRemove?: () => void;
};

const transitionProps = {
  duration: 0.3,
};

export function Thumb({ kind, url, name, onRemove }: Props) {
  const [imageState, setImageState] = useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');

  useEffect(() => {
    if (kind === 'image') {
      if (url) {
        setImageState('loading');
      } else {
        setImageState('error');
      }
    } else {
      setImageState('idle');
    }
  }, [url, kind]);

  const handleImageLoad = () => {
    setImageState('loaded');
  };

  const handleImageError = () => {
    setImageState('error');
  };

  // Wrapper to safely handle remove action with error boundary
  // This prevents crashes when removing broken/malformed images
  const handleRemove = () => {
    if (!onRemove) return;

    try {
      onRemove();
    } catch (error) {
      console.error('Error removing image:', error);
      // Optionally show user feedback here
    }
  };

  return (
    <div className='relative w-fit'>
      {onRemove && (
        <p className='absolute -right-4 -top-2.5 z-10 w-8'>
          <button
            type='button'
            className='h-8 w-8 rounded-full bg-brand text-xl text-white shadow'
            onClick={handleRemove}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </p>
      )}
      <div className='relative flex h-32 w-40 flex-col items-center justify-center gap-2 overflow-hidden rounded-xl bg-shade-10'>
        {kind === 'image' ? (
          <>
            {url && imageState === 'loading' && (
              <img
                src={url}
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{ display: 'none' }}
                alt={name || 'Loading thumbnail'}
              />
            )}

            <AnimatePresence mode='wait'>
              {imageState === 'loading' && url && (
                <motion.div
                  key='loader'
                  className='absolute inset-0 flex h-full w-full items-center justify-center'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={transitionProps}
                >
                  <SkeletonLoader />
                </motion.div>
              )}

              {imageState === 'loaded' && url && (
                <motion.img
                  key='image'
                  src={url}
                  alt={name || 'Thumbnail'}
                  className='absolute inset-0 block h-full w-full object-contain'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={transitionProps}
                />
              )}

              {imageState === 'error' && (
                <motion.div
                  key='error'
                  className='absolute inset-0 flex h-full w-full flex-col items-center justify-center text-gray-400'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={transitionProps}
                >
                  <FontAwesomeIcon
                    icon={faImageSlash as IconProp}
                    className='h-10 w-10'
                  />
                  <span className='mt-1 text-xs'>Preview N/A</span>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          <>
            <FontAwesomeIcon
              icon={name?.endsWith('.pdf') ? faFilePdf : faFile}
              className='h-10 w-10'
            />
            <span
              className={classNames('text-center leading-5', !name && 'italic')}
            >
              {name ?? 'Unknown'}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
